"use client";
import React from "react";
import Image from "next/image";
import Link from "next/link";

import { Box, Container, Typography, useMediaQuery } from "@mui/material";
import OpenTabIcon from "@mui/icons-material/OpenInNewRounded";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import WeatherComponent from "../common/Weather/WeatherComponent";

import SocialIcons from "@/components/home-redesigned/SocialIcons";

export const DestinationHero: React.FC<{
  images?: string[];
  title?: string;
  subTitle?: string;
  image?: string;
  titletop?: string;
  content?: string;
}> = ({ images, title, subTitle, image, titletop, content }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          pt: { xs: "0px", sm: "14px" },
          position: "relative",
          bgcolor: "#edf1ff",
        }}
      >
        <Container sx={{ p: { xs: "0rem 1rem 0rem 1rem", sm: "1.5rem 1.5rem 0rem 1.5rem" } }}>
          <Box
            sx={{
              "@media screen and (max-width:600px)": {
                position: "absolute",
                bottom: "0px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2,
                width: "100%",
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
              },
            }}
          >
            {titletop && (
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1rem", md: "1.4rem" },
                    fontWeight: 700,
                    color: "primary.main",
                  }}
                >
                  {titletop}
                </Typography>
              </Box>
            )}
            {title && (
              <Box
                sx={{
                  "@media screen and (max-width:600px)": {
                    bgcolor: "#fff7",
                    width: "100%",
                    backdropFilter: "blur(5px)",
                    p: "10px 16px 4px",
                    borderBottom: "2px solid #5900FF",
                    // TODO: Commented for future use
                    // border: "1px solid",
                    // borderTop: "0px solid",
                    // borderLeft: "0px solid",
                    // borderRight: "0px solid",
                    // borderImageSlice: 1,
                    // borderImageSource:
                    //   "linear-gradient(90deg,rgba(3, 0, 94, 1) 0%, rgba(0, 55, 255, 1) 100%)",
                    fontFamily: "'blauerNue', 'blauerNue Fallback'",
                    borderRadius: "25px 25px 0 0",
                  },
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" },
                    fontWeight: 700,
                    background:
                      "-webkit-linear-gradient(0deg,rgba(3, 0, 94, 1) 0%, rgba(0, 55, 255, 1) 100%);",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    width: "fit-content",
                    "@media screen and (max-width:600px)": {
                      width: "100%",
                    },
                  }}
                >
                  {title}
                </Typography>

                {isMobile && (
                  <Typography
                    variant="h2"
                    align="center"
                    gutterBottom
                    sx={{
                      color: "#E30053",
                      fontWeight: 600,
                      fontSize: { xs: "12px", sm: "16px" },
                    }}
                  >
                    Sail with Perfect Cruise
                  </Typography>
                )}
              </Box>
            )}
            {subTitle && <Typography variant="subtitle2">{subTitle}</Typography>}
            {content && (
              <Typography
                sx={{
                  mt: { xs: "0.5rem", md: "1rem" },
                  fontFamily: "roboto, roboto Fallback",
                  fontSize: "16px",
                  lineHeight: "20.8px",
                }}
              >
                {content}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              height: "100%",
              display: "grid",
              gridTemplateColumns: "auto 350px",
              gap: "25px",
              mt: { xs: "0px", sm: "1.5rem" },
              mb: { x: "1.5rem", sm: "0px" },
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                width: { xs: "calc(100% + 32px)", sm: "100%" },
                height: { xs: "200px", sm: "250px", md: "100%" },
                position: "relative",
                zIndex: 1,
                left: { xs: "-16px", sm: "unset" },
                borderRadius: { xs: "0px", sm: "20px" },
                border: { xs: "none", sm: "2px solid #fff" },
                boxShadow: "0px 2px 12px rgba(0,0,0,0.15)",
                img: {
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                  imageRendering: "high-quality",
                },
              }}
            >
              {(images?.length ?? 0) > 0 ? (
                <Swiper
                  modules={[Autoplay, Pagination]}
                  slidesPerView={1}
                  autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                  }}
                  loop
                >
                  {images
                    ?.filter((item, index, self) => index === self.findIndex((x) => x === item))
                    .map((image, index) => (
                      <SwiperSlide key={index}>
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: { xs: "250px", md: "400px" },
                          }}
                        >
                          <Image
                            src={image}
                            alt={title ? `${title} cruise` : "Destination hero photo"}
                            fill
                            style={{
                              objectFit: "cover",
                            }}
                            loader={({ src }) => src}
                            blurDataURL={image}
                            placeholder="blur"
                            unoptimized
                            priority={index === 0}
                          />
                        </Box>
                      </SwiperSlide>
                    ))}
                </Swiper>
              ) : (
                <>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Image
                      src={image ?? ""}
                      alt={title ? `${title} cruise` : "Destination hero photo"}
                      fill
                      style={{
                        objectFit: "cover",
                      }}
                      loader={({ src }) => src}
                      blurDataURL={image}
                      placeholder="blur"
                      className="animated-image"
                      unoptimized
                      priority
                    />
                  </Box>
                </>
              )}
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <WeatherComponent />
            </Box>
          </Box>
        </Container>

        <Container
          sx={{
            display: { xs: "none", sm: "block" },
            p: { xs: "1rem", sm: "1rem 24px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: { xs: "1rem", md: "1.5rem", lg: "24px" },
              mt: { xs: "0rem", md: "2rem", lg: "1rem" },
              mb: "0rem",
            }}
          >
            <Box sx={{}}>
              <Box
                sx={{
                  color: "#fff",
                  fontSize: "1rem",
                  fontWeight: 600,
                  mb: "10px",
                  mt: { xs: "10px", lg: "0px" },
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Follow Us for Exclusive Updates!
              </Box>

              <Box>
                <SocialIcons sx={{ display: "flex", justifyContent: "center" }} />
              </Box>
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" }, ml: "auto" }}>
              <Box
                sx={{
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  fontSize: "14px",
                  color: "#fff",
                  fontWeight: 400,
                  textAlign: { xs: "center", lg: "right" },
                  a: {
                    color: "#ff1184",
                    svg: {
                      mb: "-6px",
                      width: "21px",
                      height: "21px",
                      ml: "4px",
                    },
                  },
                }}
              >
                We offer flexible ways to pay for your dream cruise –{" "}
                <Link href="/payment">
                  Explore Payment Options!
                  <OpenTabIcon />
                </Link>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  textAlign: "center",
                  mt: "10px",
                  mb: "16px",
                  width: { xs: "100%", lg: "800px" },
                  img: {
                    objectFit: "contain",
                    width: "100%",
                    maxWidth: { xs: "100%", md: "900px", lg: "1200px" },
                    height: "auto",
                  },
                }}
              >
                <Image
                  src="/images/home/new-design/payment-icons-densed.png"
                  alt="Payment Icons"
                  width={800}
                  height={60}
                  loader={({ src }) => src}
                />
              </Box>
            </Box>
          </Box>
        </Container>

        <Box
          sx={{
            width: "100%",
            height: { xs: "150px", sm: "120px", md: "215px" },
            bgcolor: "#000c37",
            mt: { xs: "-145px", sm: "-90px", md: "-200px" },
            display: { xs: "none", sm: "block" },
          }}
        ></Box>
      </Box>
    </Box>
  );
};
